import type { IdentityRecord, PublicKey } from '@shared/types';
import {
  deleteDevice as deleteDeviceRecord,
  deleteIdentity as deleteIdentityRecord,
  deleteMasterKey as deleteMasterKeyRecord,
  deletePendingDeviceRegistration as deletePendingDeviceRegistrationRecord,
  deletePendingIdentityRegistration as deletePendingIdentityRegistrationRecord,
  loadPendingDeviceRegistrations as loadPendingDeviceRegistrationRecords,
  loadPendingIdentityRegistrations as loadPendingIdentityRegistrationRecords,
  markPendingIdentityRegistrationRegistered as markPendingIdentityRegistrationRegisteredRecord,
  saveDevice as saveDeviceRecord,
  saveIdentity as saveIdentityRecord,
  saveMasterKey as saveMasterKeyRecord,
  savePendingDeviceRegistration as savePendingDeviceRegistrationRecord,
  savePendingIdentityRegistration as savePendingIdentityRegistrationRecord,
  setPendingIdentityRegistrationDirectGuestContext as setPendingIdentityRegistrationDirectGuestContextRecord,
  updatePendingIdentityRegistrationActivationMeta as updatePendingIdentityRegistrationActivationMetaRecord,
  updateIdentityMeta as updateIdentityMetaRecord,
  wrapIdentityPrivateKeyForSession,
  type PendingDeviceRegistrationRecord,
  type PendingIdentityRegistrationRecord,
  type WrappedKey
} from '../crypto/storage';
import { createDeviceCryptoKeys } from '../crypto/ed25519Provider';
import { deriveVaultKey } from '../crypto/keys';
import { base64ToBytes } from '../utils/encoding';
import {
  clearBridgeMasterKeyState,
  patchBridgeIdentityState,
  persistBridgeMasterKey,
  removeBridgeDeviceState,
  removeBridgeIdentityState,
  upsertBridgeDevice,
  upsertBridgeIdentity
} from './bridgeKeyState';
import { isWebViewMode } from '../utils/webviewBridge';
import type { SigningKey } from '../crypto/ed25519Provider';
import type { IdentityAccessKind } from '../utils/identityAccess';

export async function saveMasterKey(key: CryptoKey): Promise<void> {
  if (isWebViewMode) {
    const rawBytes = new Uint8Array(await crypto.subtle.exportKey('raw', key));
    persistBridgeMasterKey(rawBytes);
    await deleteMasterKeyRecord();
  } else {
    await saveMasterKeyRecord(key);
  }
}

export async function deleteMasterKey(): Promise<void> {
  if (isWebViewMode) {
    clearBridgeMasterKeyState();
  }
  await deleteMasterKeyRecord();
}

export async function savePendingIdentityRegistration(input: {
  identityId: string;
  publicKey: PublicKey;
  privateKeyBytes: Uint8Array;
  serverUrl: string;
  flow?: PendingIdentityRegistrationRecord['flow'];
  registrationScope: string;
}): Promise<void> {
  await savePendingIdentityRegistrationRecord(input);
  upsertBridgeIdentity({
    identityId: input.identityId,
    seed: input.privateKeyBytes,
    publicKey: input.publicKey,
    serverUrl: input.serverUrl,
    pendingRegistration: true
  });
}

export async function deletePendingIdentityRegistration(identityId: string): Promise<void> {
  await deletePendingIdentityRegistrationRecord(identityId);
}

export async function markPendingIdentityRegistrationRegistered(
  identityId: string,
  result?: {
    identity?: IdentityRecord;
    activationMeta?: PendingIdentityRegistrationRecord['activationMeta'];
  }
): Promise<void> {
  await markPendingIdentityRegistrationRegisteredRecord(identityId, result);
}

export async function setPendingIdentityRegistrationDirectGuestContext(
  identityId: string,
  context: NonNullable<PendingIdentityRegistrationRecord['directGuestContext']>
): Promise<void> {
  await setPendingIdentityRegistrationDirectGuestContextRecord(identityId, context);
}

export async function updatePendingIdentityRegistrationActivationMeta(
  identityId: string,
  activationMeta: PendingIdentityRegistrationRecord['activationMeta']
): Promise<void> {
  await updatePendingIdentityRegistrationActivationMetaRecord(identityId, activationMeta);
}

export async function savePendingDeviceRegistration(input: {
  identityId: string;
  publicKey: PublicKey;
  privateKeyBytes: Uint8Array;
  encryptionPublicKey: PublicKey;
  encryptionPrivateKeyBytes: Uint8Array;
  serverUrl: string;
  label?: string;
}): Promise<string> {
  const deviceId = await savePendingDeviceRegistrationRecord(input);
  upsertBridgeDevice({
    deviceId,
    identityId: input.identityId,
    publicKey: input.publicKey,
    seed: input.privateKeyBytes,
    label: input.label,
    encryptionPublicKey: input.encryptionPublicKey,
    encryptionSeed: input.encryptionPrivateKeyBytes,
    pendingRegistration: true
  });
  return deviceId;
}

export async function deletePendingDeviceRegistration(pendingId: string): Promise<void> {
  await deletePendingDeviceRegistrationRecord(pendingId);
}

export async function loadPendingIdentityRegistrations(): Promise<PendingIdentityRegistrationRecord[]> {
  return loadPendingIdentityRegistrationRecords();
}

export async function loadPendingDeviceRegistrations(identityId: string): Promise<PendingDeviceRegistrationRecord[]> {
  return loadPendingDeviceRegistrationRecords(identityId);
}

export async function saveIdentity(
  identityId: string,
  publicKey: PublicKey,
  privateKeyBytes: Uint8Array,
  meta?: {
    serverId?: string;
    serverName?: string;
    serverUrl?: string;
    role?: string;
    accessKind?: IdentityAccessKind;
    accessContextLabel?: string;
    vpsId?: string;
    isServerAdmin?: boolean;
    isPrimaryVpsTarget?: boolean;
    fingerprint?: string;
    identityName?: string;
    publishIdentity?: boolean;
    presenceVisible?: boolean;
    noNamesOnServer?: boolean;
    useForBackupStorage?: boolean;
  }
): Promise<{ signingKey: SigningKey; ecdhKey: CryptoKey | null; ecdhScalar: Uint8Array; vaultKey: CryptoKey; wrappedPrivateKey: WrappedKey }> {
  const result = isWebViewMode
    ? {
        ...(await createDeviceCryptoKeys(privateKeyBytes, base64ToBytes(publicKey.value))),
        vaultKey: await deriveVaultKey(privateKeyBytes),
        wrappedPrivateKey: await wrapIdentityPrivateKeyForSession(privateKeyBytes)
      }
    : await saveIdentityRecord(identityId, publicKey, privateKeyBytes, meta);
  upsertBridgeIdentity({
    identityId,
    seed: privateKeyBytes,
    publicKey,
    serverUrl: meta?.serverUrl,
    serverName: meta?.serverName,
    identityName: meta?.identityName,
    publishIdentity: meta?.publishIdentity,
    presenceVisible: meta?.presenceVisible,
    role: meta?.role,
    accessKind: meta?.accessKind,
    accessContextLabel: meta?.accessContextLabel,
    vpsId: meta?.vpsId,
    isServerAdmin: meta?.isServerAdmin,
    isPrimaryVpsTarget: meta?.isPrimaryVpsTarget,
    fingerprint: meta?.fingerprint,
    serverId: meta?.serverId,
    noNamesOnServer: meta?.noNamesOnServer,
    useForBackupStorage: meta?.useForBackupStorage,
    pendingRegistration: false
  });
  if (isWebViewMode) {
    await deleteIdentityRecord(identityId);
  }
  return result;
}

export async function saveDevice(
  deviceId: string,
  identityId: string,
  publicKey: PublicKey,
  privateKeyBytes: Uint8Array,
  label?: string,
  encryptionPublicKey?: PublicKey | null,
  encryptionPrivateKeyBytes?: Uint8Array
): Promise<{ signingKey: SigningKey; ecdhKey: CryptoKey | null; ecdhScalar: Uint8Array }> {
  const result = isWebViewMode
    ? await createDeviceCryptoKeys(privateKeyBytes, base64ToBytes(publicKey.value), encryptionPrivateKeyBytes)
    : await saveDeviceRecord(deviceId, identityId, publicKey, privateKeyBytes, label, encryptionPublicKey, encryptionPrivateKeyBytes);
  upsertBridgeDevice({ deviceId, identityId, publicKey, seed: privateKeyBytes, label, encryptionPublicKey, encryptionSeed: encryptionPrivateKeyBytes, pendingRegistration: false });
  if (isWebViewMode) {
    await deleteDeviceRecord(deviceId);
  }
  return result;
}

export async function updateIdentityMeta(
  identityId: string,
  meta: Partial<{
    serverId: string;
    serverUrl: string;
    role: string;
    accessKind: IdentityAccessKind;
    accessContextLabel: string;
    serverName: string;
    identityName: string;
    publishIdentity: boolean;
    presenceVisible: boolean;
    noNamesOnServer: boolean;
    useForBackupStorage: boolean;
    vpsId: string;
    isServerAdmin: boolean;
    isPrimaryVpsTarget: boolean;
  }>
): Promise<void> {
  await updateIdentityMetaRecord(identityId, meta);
  const bridgePatch: {
    serverUrl?: string;
    serverName?: string;
    identityName?: string;
    publishIdentity?: boolean;
    presenceVisible?: boolean;
    role?: string;
    accessKind?: IdentityAccessKind;
    accessContextLabel?: string;
    vpsId?: string;
    isServerAdmin?: boolean;
    isPrimaryVpsTarget?: boolean;
  } = {};
  if (typeof meta.serverUrl === 'string') bridgePatch.serverUrl = meta.serverUrl;
  if (typeof meta.serverName === 'string') bridgePatch.serverName = meta.serverName;
  if (typeof meta.identityName === 'string') bridgePatch.identityName = meta.identityName;
  if (typeof meta.publishIdentity === 'boolean') bridgePatch.publishIdentity = meta.publishIdentity;
  if (typeof meta.presenceVisible === 'boolean') bridgePatch.presenceVisible = meta.presenceVisible;
  if (typeof meta.role === 'string') bridgePatch.role = meta.role;
  if (typeof meta.accessKind === 'string') bridgePatch.accessKind = meta.accessKind;
  if (typeof meta.accessContextLabel === 'string') bridgePatch.accessContextLabel = meta.accessContextLabel;
  if (typeof meta.vpsId === 'string') bridgePatch.vpsId = meta.vpsId;
  if (typeof meta.isServerAdmin === 'boolean') bridgePatch.isServerAdmin = meta.isServerAdmin;
  if (typeof meta.isPrimaryVpsTarget === 'boolean') bridgePatch.isPrimaryVpsTarget = meta.isPrimaryVpsTarget;
  patchBridgeIdentityState(identityId, bridgePatch);
}

export async function deleteIdentity(identityId: string): Promise<void> {
  await deleteIdentityRecord(identityId);
  removeBridgeIdentityState(identityId);
}


export async function deleteDevice(deviceId: string): Promise<void> {
  await deleteDeviceRecord(deviceId);
  removeBridgeDeviceState(deviceId);
}
